/**
 * Catálogo centralizado de pruebas (TestKey), tipos de puesto (RoleType) y presets
 * de batería por industria y rol. No duplicar strings; uso único en la app.
 */

import { isOfficialSelected } from '@/config/batteries';
import type { TestBenchmark } from '@/types';

// ─── TestKey (claves internas; no revelar nombres reales de instrumentos) ───
export const TEST_KEYS = [
  'perfil_personalidad',
  'autogestion_emocional',
  'estilo_trabajo_organizacion',
  'confiabilidad_etica',
  'estilo_decisiones',
  'responsabilidad_control',
  'criterio_operativo_normas',
  'criterio_servicio_cliente',
  'PCL_10',
  'RFB_SJ_8',
  'RA_10',
  'EEL_12',
  'JK_OFFICE_ADMIN',
  'conocimiento_puesto_ventas_retail',
  'ML_8',
  'APO_6',
  'cultura_fit',
  'NOM35_G1',
  'NOM35_G2',
  'NOM35_G3',
  'MINI_IPIP_20',
] as const;

export type TestKey = (typeof TEST_KEYS)[number];

/** Nombres comerciales (solo esto ve candidato y reclutador). */
export const TEST_DISPLAY_NAMES: Record<TestKey, string> = {
  perfil_personalidad: 'Perfil Integral de Personalidad Profesional',
  autogestion_emocional: 'Autogestión Emocional en el Trabajo',
  estilo_trabajo_organizacion: 'Estilo de Trabajo y Organización Laboral',
  confiabilidad_etica: 'Confiabilidad y Ética Profesional',
  estilo_decisiones: 'Estilo de Toma de Decisiones Laborales',
  responsabilidad_control: 'Responsabilidad y Control en el Trabajo',
  criterio_operativo_normas: 'Criterio Operativo y Apego a Normas',
  criterio_servicio_cliente: 'Criterio de Servicio y Atención al Cliente',
  PCL_10: 'Perfil de Confiabilidad Laboral (PCL-10)',
  RFB_SJ_8: 'Razonamiento Funcional (Juicio Situacional) — RFB-SJ',
  RA_10: 'Responsabilidad y Autogestión (RA-10)',
  EEL_12: 'Autogestión Emocional en el Trabajo',
  JK_OFFICE_ADMIN: 'Conocimiento del puesto — Oficina administrativa',
  conocimiento_puesto_ventas_retail: 'Conocimiento del puesto — Ventas / vendedor de piso',
  ML_8: 'Motivadores Laborales',
  APO_6: 'Atención y Precisión Operativa (APO-6)',
  cultura_fit: 'Cultura Organizacional (Ajuste Cultural)',
  NOM35_G1: 'Guía I: Acontecimientos Traumáticos Severos',
  NOM35_G2: 'Guía II: Factores de Riesgo Psicosocial (16-50 empleados)',
  NOM35_G3: 'Guía III: Entorno Organizacional (>50 empleados)',
  MINI_IPIP_20: 'Perfil Integral de Personalidad Profesional',
};

/** TestKeys que ejecutan el instrumento RA_10. Solo estilo_trabajo_organizacion es seleccionable; responsabilidad_control es indicador derivado. */
export const RA_10_ALIASES: TestKey[] = ['responsabilidad_control', 'estilo_trabajo_organizacion'];

/** TestKeys que ejecutan el instrumento EEL_12. */
export const EEL_12_ALIASES: TestKey[] = ['autogestion_emocional'];

/** Normaliza batería: responsabilidad_control/RA_10 → estilo_trabajo_organizacion (único seleccionable). */
export function normalizeSelectedTests(selectedKeys: string[]): string[] {
  const mapped = selectedKeys.map((k) => {
    if (k === 'responsabilidad_control' || k === 'RA_10') return 'estilo_trabajo_organizacion';
    return k;
  });
  const seen = new Set<string>();
  return mapped.filter((k) => {
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function getInstrumentId(testKey: string): string {
  if (RA_10_ALIASES.includes(testKey as TestKey)) return 'RA_10';
  if (testKey === 'RA_10') return 'RA_10';
  if (EEL_12_ALIASES.includes(testKey as TestKey)) return 'EEL_12';
  if (testKey === 'conocimiento_puesto_ventas_retail') return 'JK_SALES_RETAIL';
  if (testKey === 'NOM35_G1') return 'NOM35_G1';
  if (testKey === 'NOM35_G2') return 'NOM35_G2';
  if (testKey === 'NOM35_G3') return 'NOM35_G3';
  if (testKey === 'perfil_personalidad') return 'MINI_IPIP_20';
  if (testKey === 'MINI_IPIP_20') return 'MINI_IPIP_20';
  if (testKey === 'cultura_fit') return 'CULTURE_FIT';
  return testKey;
}

export function getTestDisplayName(key: string): string {
  return TEST_DISPLAY_NAMES[key as TestKey] ?? key;
}

/** Pruebas disponibles por rubro (criterio_servicio_cliente solo hospitality_food). */
export function getAvailableTestKeys(industry: string | null | undefined): TestKey[] {
  if (industry === 'hospitality_food') return [...TEST_KEYS];
  return TEST_KEYS.filter((k) => k !== 'criterio_servicio_cliente');
}

/** Tests con ítems propios implementados (runner + scoring en app). */
export const INSTRUMENT_TEST_KEYS = [
  'PCL_10',
  'RFB_SJ_8',
  'RA_10',
  'EEL_12',
  'JK_OFFICE_ADMIN',
  'JK_SALES_RETAIL',
  'ML_8',
  'APO_6',
  'cultura_fit',
  'NOM35_G1',
  'NOM35_G2',
  'NOM35_G3',
  'MINI_IPIP_20',
] as const;
export type InstrumentTestKey = (typeof INSTRUMENT_TEST_KEYS)[number];

export function isInstrumentTest(key: string): key is InstrumentTestKey {
  return INSTRUMENT_TEST_KEYS.includes(key as InstrumentTestKey);
}

// ─── RoleType (tipo de puesto del proceso; NO confundir con rubro empresarial) ───
export const ROLE_TYPE_IDS = [
  'customer_service',   // Atención a cliente (recepción, mesero, concierge)
  'cashier_pos',        // Caja / Punto de venta
  'sales_role',         // Ventas
  'operations',         // Operativo (almacén, limpieza, manufactura ligera)
  'administrative_role',// Administrativo (auxiliar, back office)
  'professional_specialist', // Profesional / Especialista
  'supervision',        // Supervisión / Mandos medios
  'management',         // Gerencia operativa (gerente de sucursal)
  'technical',          // Técnico (mantenimiento, soporte no-IT)
  'security',           // Seguridad / Control
  'driver_logistics',   // Chofer / Logística
] as const;

export type RoleType = (typeof ROLE_TYPE_IDS)[number];

export const ROLE_TYPE_LABELS: Record<RoleType, string> = {
  customer_service: 'Atención a cliente',
  cashier_pos: 'Caja / Punto de venta',
  sales_role: 'Ventas',
  operations: 'Operativo',
  administrative_role: 'Administrativo',
  professional_specialist: 'Profesional / Especialista',
  supervision: 'Supervisión / Mandos medios',
  management: 'Gerencia operativa',
  technical: 'Técnico',
  security: 'Seguridad',
  driver_logistics: 'Chofer / Logística',
};

export function getRoleTypeLabel(roleType: string | null | undefined): string {
  const normalized = normalizeRoleType(roleType);
  return ROLE_TYPE_LABELS[normalized as RoleType] ?? roleType ?? '—';
}

/** Convierte roleType legacy (sales, administrative) al ID actual. */
export function normalizeRoleType(roleType: string | null | undefined): string | null {
  if (!roleType) return null;
  const legacy: Record<string, RoleType> = {
    sales: 'sales_role',
    administrative: 'administrative_role',
  };
  return (legacy[roleType] ?? roleType) as string;
}

// ─── Cerebro Experto: Benchmarks por tipo de puesto ───
export const EXPERT_BENCHMARKS: Record<RoleType, Record<string, TestBenchmark>> = {
  sales_role: {
    JK_SALES_RETAIL: { testId: 'JK_SALES_RETAIL', minScore: 70, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 60, weight: 2 },
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 60, weight: 1 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        energiaSocial: { expectedLevel: 'Alto', weight: 3 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 2 },
        aperturaAdaptabilidad: { expectedLevel: 'Adecuado', weight: 1 },
        cooperacion: { expectedLevel: 'Indiferente', weight: 0 },
        responsabilidadOrden: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
  administrative_role: {
    JK_OFFICE_ADMIN: { testId: 'JK_OFFICE_ADMIN', minScore: 70, weight: 3 },
    APO_6: { testId: 'APO_6', minScore: 80, weight: 3 },
    RA_10: { testId: 'RA_10', minScore: 80, weight: 3 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 2 },
        aperturaAdaptabilidad: { expectedLevel: 'En desarrollo', weight: 1 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 1 },
      },
    },
  },
  professional_specialist: {
    RA_10: { testId: 'RA_10', minScore: 80, weight: 3 },
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 80, weight: 3 },
    PCL_10: { testId: 'PCL_10', minScore: 80, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        aperturaAdaptabilidad: { expectedLevel: 'Alto', weight: 2 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 1 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 1 },
      },
    },
  },
  customer_service: {
    criterio_servicio_cliente: { testId: 'criterio_servicio_cliente', minScore: 70, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 60, weight: 3 },
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 60, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        energiaSocial: { expectedLevel: 'Alto', weight: 3 },
        cooperacion: { expectedLevel: 'Alto', weight: 3 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 2 },
        aperturaAdaptabilidad: { expectedLevel: 'Adecuado', weight: 1 },
        responsabilidadOrden: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
  cashier_pos: {
    PCL_10: { testId: 'PCL_10', minScore: 80, weight: 3 },
    APO_6: { testId: 'APO_6', minScore: 80, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 60, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 2 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 2 },
        energiaSocial: { expectedLevel: 'Adecuado', weight: 1 },
        aperturaAdaptabilidad: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
  operations: {
    JK_OFFICE_ADMIN: { testId: 'JK_OFFICE_ADMIN', minScore: 70, weight: 2 },
    PCL_10: { testId: 'PCL_10', minScore: 60, weight: 3 },
    APO_6: { testId: 'APO_6', minScore: 60, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        aperturaAdaptabilidad: { expectedLevel: 'En desarrollo', weight: 2 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        cooperacion: { expectedLevel: 'Indiferente', weight: 0 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 1 },
      },
    },
  },
  supervision: {
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 70, weight: 3 },
    RA_10: { testId: 'RA_10', minScore: 80, weight: 2 },
    EEL_12: { testId: 'EEL_12', minScore: 70, weight: 2 },
    PCL_10: { testId: 'PCL_10', minScore: 60, weight: 1 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        estabilidadEmocional: { expectedLevel: 'Alto', weight: 2 },
        energiaSocial: { expectedLevel: 'Adecuado', weight: 2 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 1 },
        aperturaAdaptabilidad: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
  management: {
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 80, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 80, weight: 2 },
    RA_10: { testId: 'RA_10', minScore: 80, weight: 2 },
    PCL_10: { testId: 'PCL_10', minScore: 60, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        estabilidadEmocional: { expectedLevel: 'Alto', weight: 3 },
        aperturaAdaptabilidad: { expectedLevel: 'Alto', weight: 2 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 1 },
        energiaSocial: { expectedLevel: 'Adecuado', weight: 1 },
      },
    },
  },
  technical: {
    RFB_SJ_8: { testId: 'RFB_SJ_8', minScore: 60, weight: 2 },
    RA_10: { testId: 'RA_10', minScore: 60, weight: 2 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 2 },
        estabilidadEmocional: { expectedLevel: 'Adecuado', weight: 2 },
        aperturaAdaptabilidad: { expectedLevel: 'Adecuado', weight: 1 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        cooperacion: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
  security: {
    PCL_10: { testId: 'PCL_10', minScore: 80, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 80, weight: 3 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        estabilidadEmocional: { expectedLevel: 'Alto', weight: 3 },
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        aperturaAdaptabilidad: { expectedLevel: 'En desarrollo', weight: 2 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 1 },
      },
    },
  },
  driver_logistics: {
    PCL_10: { testId: 'PCL_10', minScore: 70, weight: 3 },
    EEL_12: { testId: 'EEL_12', minScore: 60, weight: 2 },
    APO_6: { testId: 'APO_6', minScore: 60, weight: 1 },
    MINI_IPIP_20: {
      testId: 'MINI_IPIP_20',
      dimensions: {
        responsabilidadOrden: { expectedLevel: 'Alto', weight: 3 },
        estabilidadEmocional: { expectedLevel: 'Alto', weight: 2 },
        energiaSocial: { expectedLevel: 'Indiferente', weight: 0 },
        cooperacion: { expectedLevel: 'Adecuado', weight: 1 },
        aperturaAdaptabilidad: { expectedLevel: 'Indiferente', weight: 0 },
      },
    },
  },
};

export function getExpertBenchmark(role: RoleType): Record<string, TestBenchmark> | null {
  return EXPERT_BENCHMARKS[role] ?? null;
}
